import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { ParentApi } from '../../services/api/ParentApi';
import './ParentOnboarding.css';

function ParentOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [studentTab, setStudentTab] = useState('connect');
  const [connectEmail, setConnectEmail] = useState('');
  const [studentName, setStudentName] = useState('');
  const [alertPrefs, setAlertPrefs] = useState({
    missingAssignments: true,
    dueWithin24Hours: true,
    dueWithin3Days: false,
    lowWeeklyActivity: true,
    decliningCompletion: true,
    noRecentLogin: false,
    courseRisk: true,
    weeklySummary: true,
    positiveMilestones: true,
  });

  const totalSteps = 4;

  const handleFinish = async () => {
    try {
      await ParentApi.becomeParent();
      if (connectEmail && connectEmail.trim()) {
        await ParentApi.inviteChild({ email: connectEmail.trim(), firstName: studentName || undefined });
      }
    } catch (e) {
      // Non-fatal: still enter the portal; the parent can invite from Students.
      console.warn('Parent onboarding finish error', e);
    }
    navigate('/parent/overview');
  };

  return (
    <div className="parent-onboarding">
      <div className="parent-onboarding__card">
        {/* Progress */}
        <div className="parent-onboarding__progress">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`parent-onboarding__step-dot ${s <= step ? 'parent-onboarding__step-dot--active' : ''} ${s < step ? 'parent-onboarding__step-dot--complete' : ''}`}>
              {s < step ? <FiCheck size={12} /> : s}
            </div>
          ))}
          <div className="parent-onboarding__progress-bar">
            <div className="parent-onboarding__progress-fill" style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }} />
          </div>
        </div>

        {/* Step 1: Welcome */}
        {step === 1 && (
          <div className="parent-onboarding__content">
            <h1>Welcome to the Parent Portal</h1>
            <p className="parent-onboarding__subtitle">
              Stay informed without hovering. DocuHounds gives you a clear view of upcoming assignments, study activity, completed work, and signs that your student may need support.
            </p>
            <div className="parent-onboarding__preview">
              <div className="parent-onboarding__preview-card">📊 Assignment Status</div>
              <div className="parent-onboarding__preview-card">📚 Study Activity</div>
              <div className="parent-onboarding__preview-card">🔔 Smart Alerts</div>
              <div className="parent-onboarding__preview-card">📈 Weekly Reports</div>
            </div>
          </div>
        )}

        {/* Step 2: Add Student */}
        {step === 2 && (
          <div className="parent-onboarding__content">
            <h1>Connect a Student</h1>
            <p className="parent-onboarding__subtitle">Link your student's account to see their academic activity.</p>

            <div className="parent-onboarding__tabs">
              <button className={`parent-onboarding__tab ${studentTab === 'connect' ? 'parent-onboarding__tab--active' : ''}`} onClick={() => setStudentTab('connect')}>
                Connect Existing
              </button>
              <button className={`parent-onboarding__tab ${studentTab === 'create' ? 'parent-onboarding__tab--active' : ''}`} onClick={() => setStudentTab('create')}>
                Create Profile
              </button>
            </div>

            {studentTab === 'connect' ? (
              <div className="parent-onboarding__form">
                <div className="parent-onboarding__field">
                  <label>Student Email</label>
                  <input type="email" value={connectEmail} onChange={e => setConnectEmail(e.target.value)} placeholder="student@school.edu" />
                </div>
                <div className="parent-onboarding__field">
                  <label>Connection Code (optional)</label>
                  <input type="text" placeholder="Enter code from student" />
                </div>
              </div>
            ) : (
              <div className="parent-onboarding__form">
                <div className="parent-onboarding__field">
                  <label>Student Name</label>
                  <input type="text" value={studentName} onChange={e => setStudentName(e.target.value)} placeholder="First and last name" />
                </div>
                <div className="parent-onboarding__field">
                  <label>Grade</label>
                  <select><option>Grade 9</option><option>Grade 10</option><option>Grade 11</option><option>Grade 12</option></select>
                </div>
                <div className="parent-onboarding__field">
                  <label>School</label>
                  <input type="text" placeholder="School name" />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Alert Preferences */}
        {step === 3 && (
          <div className="parent-onboarding__content">
            <h1>Alert Preferences</h1>
            <p className="parent-onboarding__subtitle">Choose which situations you'd like to be notified about.</p>

            <div className="parent-onboarding__alert-list">
              {Object.entries({
                missingAssignments: 'Missing assignment alerts',
                dueWithin24Hours: 'Assignment due within 24 hours',
                dueWithin3Days: 'Assignment due within 3 days',
                lowWeeklyActivity: 'Low weekly activity',
                decliningCompletion: 'Declining completion rate',
                noRecentLogin: 'No recent login',
                courseRisk: 'Course risk alerts',
                weeklySummary: 'Weekly progress summary',
                positiveMilestones: 'Positive milestone alerts',
              }).map(([key, label]) => (
                <label key={key} className="parent-onboarding__alert-toggle">
                  <input
                    type="checkbox"
                    checked={alertPrefs[key]}
                    onChange={() => setAlertPrefs(prev => ({ ...prev, [key]: !prev[key] }))}
                  />
                  <span className="parent-onboarding__alert-toggle-switch" />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Dashboard Preview */}
        {step === 4 && (
          <div className="parent-onboarding__content">
            <h1>You're All Set!</h1>
            <p className="parent-onboarding__subtitle">Here's a preview of what you'll see in your Parent Portal.</p>

            <div className="parent-onboarding__dashboard-preview">
              <div className="parent-onboarding__preview-section">
                <span className="parent-onboarding__preview-label">Academic Status</span>
                <span className="parent-onboarding__preview-value" style={{ color: '#22c55e' }}>On Track</span>
              </div>
              <div className="parent-onboarding__preview-section">
                <span className="parent-onboarding__preview-label">Upcoming Work</span>
                <span className="parent-onboarding__preview-value">4 assignments this week</span>
              </div>
              <div className="parent-onboarding__preview-section">
                <span className="parent-onboarding__preview-label">Study Engagement</span>
                <span className="parent-onboarding__preview-value">5 active days</span>
              </div>
              <div className="parent-onboarding__preview-section">
                <span className="parent-onboarding__preview-label">Alerts</span>
                <span className="parent-onboarding__preview-value">1 item needs attention</span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="parent-onboarding__nav">
          {step > 1 && (
            <button className="parent-onboarding__nav-btn parent-onboarding__nav-btn--back" onClick={() => setStep(s => s - 1)}>
              <FiArrowLeft size={16} /> Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < totalSteps ? (
            <button className="parent-onboarding__nav-btn parent-onboarding__nav-btn--next" onClick={() => setStep(s => s + 1)}>
              Continue <FiArrowRight size={16} />
            </button>
          ) : (
            <button className="parent-onboarding__nav-btn parent-onboarding__nav-btn--next" onClick={handleFinish}>
              Go to Dashboard <FiArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ParentOnboarding;
